import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  isStandalone,
  getPWAPlatform,
  getDismissedAt,
  setDismissedNow,
  canShowAfterDismiss,
  PWA_RE_SHOW_AFTER_DAYS,
} from '../../utils/pwaInstall';

/**
 * Hook for PWA install prompt behavior.
 * - Detects platform (iOS / Android / desktop).
 * - Detects standalone (already installed).
 * - On Android: captures beforeinstallprompt and exposes install().
 * - Persists dismissal in localStorage and optionally re-shows after N days.
 *
 * @param {Object} [options]
 * @param {number} [options.reShowAfterDays] Re-show prompt after this many days (default: PWA_RE_SHOW_AFTER_DAYS). 0 = never re-show.
 * @returns {{
 *   shouldShow: boolean,
 *   platform: 'ios'|'android'|'desktop',
 *   isIOS: boolean,
 *   isAndroid: boolean,
 *   deferredPrompt: BeforeInstallPromptEvent | null,
 *   install: () => Promise<{ outcome: string } | null>,
 *   dismiss: () => void,
 *   dismissedAt: number | null,
 * }}
 */
export function usePWAInstall(options = {}) {
  const { reShowAfterDays = PWA_RE_SHOW_AFTER_DAYS } = options;

  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [standalone, setStandalone] = useState(false);
  const [dismissedAt, setDismissedAt] = useState(null);

  const platform = useMemo(
    () => (typeof window !== 'undefined' ? getPWAPlatform() : 'desktop'),
    []
  );
  const isIOS = platform === 'ios';
  const isAndroid = platform === 'android';
  const isMobile = isIOS || isAndroid;

  // Sync standalone and dismissed state (e.g. after storage event or initial load)
  useEffect(() => {
    setStandalone(isStandalone());
    setDismissedAt(getDismissedAt());
  }, []);

  // Listen for beforeinstallprompt (Android/Chrome)
  useEffect(() => {
    if (!isAndroid) return;

    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
  }, [isAndroid]);

  const install = useCallback(async () => {
    if (!deferredPrompt) return null;
    await deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    return choice;
  }, [deferredPrompt]);

  const dismiss = useCallback(() => {
    setDismissedNow();
    setDismissedAt(Date.now());
  }, []);

  const canShow = canShowAfterDismiss(reShowAfterDays);
  // iOS: show instructions. Android: show only when we have beforeinstallprompt so the Install button works.
  const shouldShow =
    isMobile && !standalone && canShow && (isIOS || (isAndroid && deferredPrompt !== null));

  return {
    shouldShow,
    platform,
    isIOS,
    isAndroid,
    deferredPrompt,
    install,
    dismiss,
    dismissedAt,
  };
}

export default usePWAInstall;
